import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Componente de paginación reutilizable.
 *
 * Props:
 *   page         {number}   — página actual (1-based)
 *   totalPages   {number}   — total de páginas
 *   total        {number}   — total de registros
 *   pageSize     {number}   — registros por página
 *   onPageChange {function} — callback(newPage)
 */
export default function Pagination({ page, totalPages, total, pageSize, onPageChange }) {
  if (totalPages <= 1) return null;

  const from = (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);

  // Genera el array de páginas visibles con ellipsis
  const pages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const delta = 1;
    const range = [];
    for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
      range.push(i);
    }
    const result = [1];
    if (range[0] > 2) result.push('…');
    result.push(...range);
    if (range[range.length - 1] < totalPages - 1) result.push('…');
    result.push(totalPages);
    return result;
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
      {/* Info */}
      <span className="text-xs text-gray-400">
        {from}–{to} de {total.toLocaleString()}
      </span>

      {/* Controles */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft size={14} />
        </button>

        {pages().map((p, i) =>
          p === '…' ? (
            <span key={`e${i}`} className="w-8 text-center text-xs text-gray-300">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`w-8 h-8 text-xs rounded-lg transition-colors ${
                p === page
                  ? 'bg-blue-600 text-white font-semibold'
                  : 'border border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
            >
              {p}
            </button>
          )
        )}

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className="p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
